import React, { useCallback, useMemo } from "react";
import {
  DataTable,
  GROUPED_TABLE_CLASS_NAME,
  GroupHeaderRow,
  getGroupedRowClassName,
  useGroupedRows,
  cn,
  Hyperlink,
  PlateNumber,
  type Column,
  type Row,
  type SortingState,
} from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { useNavigate } from "react-router-dom";
import { useTableSettings } from "~/shared/hooks/useTableSettings";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { IcWhatsapp } from "@racoon/icons";
import { EMPTY_CELL, reformatPhoneNumber } from "@racoon/lib";
import { useCustomerCardModal } from "~/features/customer-card/hooks/use-customer-card-modal";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";
import { useOpenContract } from "~/features/customer-card/hooks/use-open-contract";
import { PickupLocationGroupHeader } from "./pickup-location-group-header";
import {
  containerGroupLabel,
  contactLabel,
  contractLabel,
  contactPhone,
  customerLabel,
  flattenLocations,
  locationGroupKey,
  locationRowKey,
  pickupScheduleLabel,
  routeGroupsLabel,
  trucksLabel,
  type CgRouteIndex,
  type PickupLocationRow,
} from "../lib/cascade-rows";
import { LOCATION_SORT_FIELDS } from "../lib/location-list";

// Sorting compares the accessor value, and the grouped sort sinks only `undefined` to
// the bottom: a column whose label is the empty placeholder sorts as missing, while its
// cell still renders the label.
const sortValue = (label: string) => (label === EMPTY_CELL || label === "" ? undefined : label);

/** The crew and schedule cells sit in the middle of their column; the header stays left. */
const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex w-full flex-wrap items-center justify-center gap-1.5">{children}</div>
);

/**
 * The pickup-locations cascade: one column header at the top, then a
 * collapsible card per location holding its container groups — the same
 * single-`DataTable` pattern as autopark's `ShiftsCascade`, which keeps every
 * group's columns aligned under one shared header.
 */
export const PickupLocationsTable: React.FC<{
  locations: ConnectProto.PickupLocation[];
  /** Container group id → its route groups and their trucks (client-side join). */
  routeIndex: CgRouteIndex;
  loading?: boolean;
  /** Loads the next page when the last row scrolls into view. */
  onEndReached?: () => void;
  /** True while the next page is loading. */
  loadingMore?: boolean;
  onLocationClick?: (location: ConnectProto.PickupLocation) => void;
  onContainerGroupClick?: (
    cg: ConnectProto.ContainerGroup,
    location: ConnectProto.PickupLocation,
  ) => void;
  /** Location pointed at by the `highlightLocation` URL param, if any. */
  highlightLocationId?: string | null;
  /**
   * The column sort, held by the page: a sort orders the whole list of locations, on the
   * server while the list is paged.
   */
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  /**
   * Order the locations themselves by the sorted column, for a list loaded in full.
   * Off, they keep the order they came in — the server's — and only each location's
   * container groups are sorted here.
   */
  sortLocations: boolean;
}> = ({
  locations,
  routeIndex,
  loading,
  onEndReached,
  loadingMore,
  onLocationClick,
  onContainerGroupClick,
  highlightLocationId,
  sorting,
  onSortingChange,
  sortLocations,
}) => {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const navigate = useNavigate();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    "pickup_locations_cascade",
    selectedCompanyId || undefined,
  );

  // A container group is served for one customer; its name opens that
  // customer's card over the registry.
  const { openSingle: openCustomerCard } = useCustomerCardModal({
    providerId: selectedCompanyId ?? undefined,
  });
  // Another provider's site (a zone's, or one not yet known as ours) opens no customer or contract card
  const isOwnForWrite = useIsOwnOrganizationForWrite();
  const openContract = useOpenContract();

  const columns = useMemo<Column<PickupLocationRow>[]>(
    () =>
      (
        [
          {
            id: "containerGroup",
            header: t(Namespace.PickupLocation, "groups.table.containerGroups"),
            width: 150,
            // Without a container type the group sorts as missing, as on the server.
            accessor: (r) => (r.cg?.containerType?.name ? containerGroupLabel(r) : undefined),
            cell: ({ row }) => {
              const { cg, location } = row.original;
              if (!cg) return containerGroupLabel(row.original);
              return (
                <Hyperlink
                  className="truncate"
                  onOpen={() => onContainerGroupClick?.(cg, location)}
                >
                  {containerGroupLabel(row.original)}
                </Hyperlink>
              );
            },
          },
          {
            id: "routeGroup",
            header: t(Namespace.PickupLocation, "table.routeGroup"),
            width: 220,
            accessor: (r) => sortValue(routeGroupsLabel(r)),
            // Each route group opens on the route-groups page.
            cell: ({ row }) => {
              const route = row.original.route;
              if (!route?.groupIds.length) return EMPTY_CELL;
              return (
                <div className="flex min-w-0 flex-wrap gap-x-2">
                  {route.groupIds.map((id, index) => (
                    <Hyperlink
                      key={id}
                      className="truncate"
                      onOpen={() =>
                        navigate(`/${selectedCompanyId}/pickup-location-groups?group=${id}`)
                      }
                    >
                      {route.groupNames[index]}
                    </Hyperlink>
                  ))}
                </div>
              );
            },
          },
          {
            id: "responsibleTruck",
            header: t(Namespace.PickupLocation, "groups.responsibleTruck"),
            width: 220,
            accessor: (r) => sortValue(trucksLabel(r)),
            cell: ({ row }) => {
              const plates = row.original.route?.truckPlates ?? [];
              return (
                <Centered>
                  {plates.length === 0
                    ? EMPTY_CELL
                    : plates.map((plate) => <PlateNumber key={plate} plateNumber={plate} />)}
                </Centered>
              );
            },
          },
          {
            id: "customer",
            header: t(Namespace.PickupLocation, "table.customer"),
            width: 220,
            accessor: (r) => sortValue(customerLabel(r)),
            cell: ({ row }) => {
              const org = row.original.cg?.ownerOrganization;
              if (!org) return EMPTY_CELL;
              const { ownerOrganizationId, serviceProviderId } = row.original.location;
              if (isOwnForWrite(ownerOrganizationId, serviceProviderId) !== true)
                return <span className="truncate">{customerLabel(row.original)}</span>;
              return (
                <Hyperlink className="truncate" onOpen={() => openCustomerCard(org)}>
                  {customerLabel(row.original)}
                </Hyperlink>
              );
            },
          },
          {
            id: "contract",
            header: t(Namespace.PickupLocation, "table.contract"),
            width: 220,
            accessor: (r) => sortValue(contractLabel(r, t)),
            // The contract opens its card over the cascade.
            cell: ({ row }) => {
              const { cg, location } = row.original;
              if (
                !cg?.contract ||
                isOwnForWrite(location.ownerOrganizationId, location.serviceProviderId) !== true
              )
                return contractLabel(row.original, t);
              return (
                <Hyperlink
                  className="truncate"
                  onOpen={() => openContract(cg.contract!, cg.ownerOrganizationId)}
                >
                  {contractLabel(row.original, t)}
                </Hyperlink>
              );
            },
          },
          {
            id: "contact",
            header: t(Namespace.Table, "contact"),
            width: 220,
            accessor: (r) => sortValue(contactLabel(r)),
            cell: ({ row }) => contactLabel(row.original),
          },
          {
            id: "contactNumber",
            header: t(Namespace.Table, "contactNumber"),
            width: 220,
            accessor: (r) => sortValue(contactPhone(r)),
            cell: ({ row }) => {
              const phone = contactPhone(row.original);
              if (!phone) return EMPTY_CELL;
              return (
                <div className="flex items-center gap-1.5">
                  <IcWhatsapp className="w-4 h-4 text-green-500 shrink-0" />
                  <Hyperlink
                    className="truncate"
                    href={`https://wa.me/${reformatPhoneNumber(phone)}`}
                  >
                    {phone}
                  </Hyperlink>
                </div>
              );
            },
          },
          {
            id: "pickupSchedule",
            header: t(Namespace.PickupLocation, "schedule.title"),
            width: 140,
            accessor: (r) => pickupScheduleLabel(r, t),
            cell: ({ row }) => <Centered>{pickupScheduleLabel(row.original, t)}</Centered>,
          },
        ] satisfies Column<PickupLocationRow>[]
      ).map((column) => ({
        ...column,
        // A column sorts only where the server can order the locations by it.
        sortable: column.id in LOCATION_SORT_FIELDS,
        // The first row is a location header with no value, which would make
        // TanStack start every column descending: start ascending instead.
        sortDescFirst: false,
      })),
    [
      t,
      openCustomerCard,
      openContract,
      isOwnForWrite,
      onContainerGroupClick,
      navigate,
      selectedCompanyId,
    ],
  );

  const rows = useMemo(() => flattenLocations(locations, routeIndex), [locations, routeIndex]);

  // Every location opens collapsed; the one a deep link points at opens by itself.
  const defaultExpanded = useCallback(
    (header: PickupLocationRow) => String(header.location.id) === highlightLocationId,
    [highlightLocationId],
  );
  const { data, isGroupExpanded, toggleGroup } = useGroupedRows<PickupLocationRow>({
    rows,
    columns,
    sorting,
    defaultExpanded,
    sortGroups: sortLocations,
  });

  const renderCustomRow = useCallback(
    (row: Row<PickupLocationRow>, _index: number, visibleColumnCount: number) => {
      const locationRow = row.original;
      if (!locationRow._isGroupHeader) return undefined;

      const { location } = locationRow;
      const hasChildren = (location.containerGroups?.length ?? 0) > 0;
      const expanded = hasChildren && isGroupExpanded(locationRow);
      const highlighted = String(location.id) === highlightLocationId;

      return (
        <GroupHeaderRow
          key={locationGroupKey(location)}
          colSpan={visibleColumnCount}
          isExpanded={expanded}
          onToggle={() => hasChildren && toggleGroup(locationRow)}
          className={cn(
            // Same hover the route-list cascade gives its group headers, and
            // only where there is something to open.
            // `!`: GroupHeaderRow already sets cursor-pointer and bg-white, and `cn` is a
            // plain join, so without it stylesheet order would pick the winner.
            hasChildren ? "hover:bg-muted/10" : "!cursor-default",
            highlighted && "!bg-blue-50",
            // Measured off the design: a ~6px fade under the header. `relative` lifts the
            // cell above the next row, whose background would otherwise paint over it.
            expanded && "relative z-[1] shadow-[0_2px_6px_rgba(0,0,0,0.06)]",
          )}
        >
          <PickupLocationGroupHeader
            location={location}
            isExpanded={expanded}
            isHighlighted={highlighted}
            onNameClick={() => onLocationClick?.(location)}
          />
        </GroupHeaderRow>
      );
    },
    [isGroupExpanded, toggleGroup, onLocationClick, highlightLocationId],
  );

  return (
    <div className="w-full min-w-0 flex flex-col min-h-0 flex-1">
      <DataTable<PickupLocationRow>
        columns={columns}
        data={data}
        loading={loading}
        onEndReached={onEndReached}
        loadingMore={loadingMore}
        rowKey={locationRowKey}
        renderCustomRow={renderCustomRow}
        // Sorted by `useGroupedRows`, so each location's header stays pinned above its
        // container groups while the locations themselves are reordered.
        manualSorting
        onSortChange={onSortingChange}
        // The links in a row open what they name; anywhere else opens the location's card.
        onRowClick={(row) => onLocationClick?.(row.location)}
        // A group's rows ease in as it opens, over the 200ms the route-list
        // cascade takes to unfold.
        // The design's rows: 48px for one line (8px padding around a 32px line), the
        // first column 8px in, a 1px line between rows. `!` beats TableCell's py-4 and
        // the grouped rows' pl-4, since `cn` is a plain join.
        rowClassName={(row) =>
          cn(
            getGroupedRowClassName(row),
            !row._isGroupHeader &&
              "h-12 [&>td]:!py-2 [&>td:first-child]:!pl-2 animate-in fade-in-0 slide-in-from-top-1 duration-200",
            !row._isGroupHeader && !row._isLastInGroup && "[&>td]:border-b",
          )
        }
        className={GROUPED_TABLE_CLASS_NAME}
        columnConfig={columnConfig}
        onColumnConfigChange={handleColumnConfigChange}
        namespace={Namespace.Table}
        selectable={false}
      />
    </div>
  );
};
